import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from './Layout';
import { usersAPI, campaignsAPI, campaignProvisionsAPI, quarterlyProvisionsAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Banknote,
  Calculator,
  FileText,
  Calendar,
  Clock,
  Loader2,
  Users,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  BellRing
} from 'lucide-react';
import ProvisionManagerExtended from './ProvisionManagerExtended';
import GehaltsrechnerTrainee from './GehaltsrechnerTrainee';
import DienstplanGenerator from './DienstplanGenerator';
import TagesplanungView from './TagesplanungView';
import TimelineDashboard from './TimelineDashboard';
import { Input } from './ui/input';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(value || 0);
};

const trainerDotColors = [
  '#3b82f6', '#10b981', '#f97316', '#a855f7', '#f43f5e',
  '#06b6d4', '#f59e0b', '#6366f1', '#ec4899', '#14b8a6'
];

const AdminDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('provision');
  const [selectedYear, setSelectedYear] = useState('2025');
  const [loading, setLoading] = useState(true);
  const [trainers, setTrainers] = useState([]);
  const [campaignsById, setCampaignsById] = useState({});
  const [provisionsByTrainer, setProvisionsByTrainer] = useState({});
  const [quarterlyByTrainer, setQuarterlyByTrainer] = useState({});

  // Modal states
  const [provisionModalOpen, setProvisionModalOpen] = useState(false);
  const [modalTrainer, setModalTrainer] = useState('');
  const [modalCampaign, setModalCampaign] = useState('');
  const [gehaltsrechnerOpen, setGehaltsrechnerOpen] = useState(false);
  const [dienstplanOpen, setDienstplanOpen] = useState(false);
  const [tagesplanungOpen, setTagesplanungOpen] = useState(false);
  const [payoutThreshold, setPayoutThreshold] = useState(
    localStorage.getItem('wahr_payout_threshold') || '100'
  );

  const updateThreshold = (value) => {
    setPayoutThreshold(value);
    localStorage.setItem('wahr_payout_threshold', value);
  };

  useEffect(() => {
    loadData();
  }, [selectedYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      const year = parseInt(selectedYear);
      const [usersData, campaignsData, allProvisions] = await Promise.all([
        usersAPI.getAll(),
        campaignsAPI.getAll(year),
        campaignProvisionsAPI.getAll(year)
      ]);

      const trainersList = usersData.filter(u => u.role === 'trainer');
      setTrainers(trainersList);

      const campMap = {};
      campaignsData.forEach(c => { campMap[c.id] = c; });
      setCampaignsById(campMap);

      const byTrainer = {};
      allProvisions.forEach(p => {
        if (!byTrainer[p.user_id]) byTrainer[p.user_id] = [];
        byTrainer[p.user_id].push(p);
      });
      setProvisionsByTrainer(byTrainer);

      // Quarterly provisions per trainer
      const quarterly = {};
      await Promise.all(trainersList.map(async (t) => {
        quarterly[t.id] = {};
        await Promise.all([1, 2, 3, 4].map(async (q) => {
          try {
            const qp = await quarterlyProvisionsAPI.get(t.id, year, q);
            if (qp && qp.total_provision_brutto > 0) {
              quarterly[t.id][q] = {
                brutto: qp.total_provision_brutto,
                netto: qp.total_provision_netto || 0,
                units: qp.total_units || 0,
                ug_factor: qp.ug_factor,
                ug_mwst_rate: qp.ug_mwst_rate,
                remaining: typeof qp.remaining_amount === 'number' ? qp.remaining_amount : qp.total_provision_brutto
              };
            }
          } catch (e) { /* keine Quartalsprovision */ }
        }));
      }));
      setQuarterlyByTrainer(quarterly);
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
    setLoading(false);
  };

  const uniqueTraineeCount = (trainees) => {
    const names = new Set((trainees || []).map(t => (t.name || '').trim().toLowerCase()).filter(Boolean));
    return names.size || (trainees?.length || 0);
  };

  const getCampaignLabel = (campaignId) => {
    const c = campaignsById[campaignId];
    if (c) {
      const weeks = c.calendar_weeks || [];
      if (weeks.length >= 1) {
        return `KW ${Math.min(...weeks)}-${Math.max(...weeks)}`;
      }
      return c.name;
    }
    const m = String(campaignId).match(/kw-?(\d+)-(\d+)/i);
    if (m) return `KW ${m[1]}-${m[2]}`;
    return campaignId;
  };

  const getTrainerSummary = (trainerId) => {
    const provs = (provisionsByTrainer[trainerId] || []).filter(
      p => (p.total_provision_brutto || 0) > 0 || (p.trainees || []).length > 0
    );
    const quarterly = quarterlyByTrainer[trainerId] || {};
    const campaignTotal = provs.reduce((sum, p) => sum + (p.total_provision_brutto || 0), 0);
    const campaignNetto = provs.reduce((sum, p) => sum + (p.total_provision_netto || 0), 0);
    const quarterlyTotal = Object.values(quarterly).reduce((s, v) => s + v.brutto, 0);
    const quarterlyNetto = Object.values(quarterly).reduce((s, v) => s + v.netto, 0);
    return {
      provs,
      quarterly,
      yearTotal: campaignTotal + quarterlyTotal,
      yearNetto: campaignNetto + quarterlyNetto
    };
  };

  const getPayoutStatus = (p) => {
    const total = p.total_provision_brutto || 0;
    const paid = (p.total_payouts || 0) + (p.total_advances || 0);
    const remaining = typeof p.remaining_amount === 'number' ? p.remaining_amount : total - paid;
    return { total, paid, remaining, isPaid: remaining <= 0 && total > 0 };
  };

  const openTrainerProvision = (trainerId, campaignId) => {
    setModalTrainer(trainerId);
    setModalCampaign(campaignId || '');
    setProvisionModalOpen(true);
  };

  const grandTotal = trainers.reduce((sum, t) => sum + getTrainerSummary(t.id).yearTotal, 0);
  const grandNetto = trainers.reduce((sum, t) => sum + getTrainerSummary(t.id).yearNetto, 0);

  // Payout reminders: trainers with open amounts above threshold
  const getOpenAmount = (trainerId) => {
    const { provs, quarterly } = getTrainerSummary(trainerId);
    const campaignOpen = provs.reduce((s, p) => s + Math.max(getPayoutStatus(p).remaining, 0), 0);
    const quarterlyOpen = Object.values(quarterly).reduce((s, v) => s + Math.max(v.remaining, 0), 0);
    return campaignOpen + quarterlyOpen;
  };

  const threshold = parseFloat(payoutThreshold) || 0;
  const payoutReminders = trainers
    .map(t => ({ trainer: t, open: getOpenAmount(t.id) }))
    .filter(x => x.open >= threshold && x.open > 0)
    .sort((a, b) => b.open - a.open);

  // WAHR Institut / UG: Faktor + USt sind pro Kampagne/Quartal anpassbar (Default: 2,00 € + 19%)
  const totalUnits = trainers.reduce((sum, t) => {
    const { provs, quarterly } = getTrainerSummary(t.id);
    return sum
      + provs.reduce((s, p) => s + (p.total_units || 0), 0)
      + Object.values(quarterly).reduce((s, v) => s + (v.units || 0), 0);
  }, 0);
  let institutNetto = 0;
  let institutBrutto = 0;
  trainers.forEach(t => {
    const { provs, quarterly } = getTrainerSummary(t.id);
    provs.forEach(p => {
      const n = (p.total_units || 0) * (p.ug_factor ?? 2.0);
      institutNetto += n;
      institutBrutto += n * (1 + (p.ug_mwst_rate ?? 0.19));
    });
    Object.values(quarterly).forEach(v => {
      const n = (v.units || 0) * (v.ug_factor ?? 2.0);
      institutNetto += n;
      institutBrutto += n * (1 + (v.ug_mwst_rate ?? 0.19));
    });
  });
  const ugNetto = institutNetto - grandNetto;
  const ugBrutto = institutBrutto - grandTotal;
  const ugForProvision = (units, traineeBrutto, factor, mwst) =>
    (units || 0) * (factor ?? 2.0) * (1 + (mwst ?? 0.19)) - (traineeBrutto || 0);

  return (
    <Layout>
      <div className="min-h-screen">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1c2e52] to-[#3e4350] text-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-xl mb-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">Hallo, {user?.name?.split(' ')[0]}!</h1>
                <p className="text-sm text-[#e6d7bf]">Admin Dashboard</p>
              </div>
              <div className="flex items-center gap-3">
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-[100px] bg-white/10 border-white/20 text-white" data-testid="admin-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2024">2024</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2026">2026</SelectItem>
                  </SelectContent>
                </Select>
                <Badge className="bg-white/20 text-white text-sm hidden sm:flex">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Netto: {formatCurrency(grandNetto)} · Brutto: {formatCurrency(grandTotal)}
                </Badge>
              </div>
            </div>

            {/* Navigation Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid grid-cols-5 gap-1 bg-white/10 p-1 rounded-lg">
                <TabsTrigger
                  value="provision"
                  data-testid="admin-tab-provision"
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1c2e52] text-white text-xs sm:text-sm"
                >
                  <Banknote className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Provision</span>
                </TabsTrigger>
                <TabsTrigger
                  value="gehaltsrechner"
                  data-testid="admin-tab-rechner"
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1c2e52] text-white text-xs sm:text-sm"
                >
                  <Calculator className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Rechner</span>
                </TabsTrigger>
                <TabsTrigger
                  value="wochenplan"
                  data-testid="admin-tab-wochenplan"
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1c2e52] text-white text-xs sm:text-sm"
                >
                  <FileText className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Wochenplan</span>
                </TabsTrigger>
                <TabsTrigger
                  value="tagesplanung"
                  data-testid="admin-tab-tagesplan"
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1c2e52] text-white text-xs sm:text-sm"
                >
                  <Calendar className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Tagesplan</span>
                </TabsTrigger>
                <TabsTrigger
                  value="zeitstrahl"
                  data-testid="admin-tab-zeitstrahl"
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1c2e52] text-white text-xs sm:text-sm"
                >
                  <Clock className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Zeitstrahl</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Content */}
        <div className={activeTab === 'zeitstrahl' ? '' : 'max-w-6xl mx-auto'}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            {/* Provision Tab - All Trainers Overview */}
            <TabsContent value="provision" className="mt-0">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-[#4b95d1]" />
                </div>
              ) : trainers.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Keine Trainer gefunden</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4" data-testid="admin-provision-overview">
                  {/* WAHR Institut / UG Overview Card */}
                  <Card className="border-[#1c2e52]/30 bg-gradient-to-r from-[#1c2e52]/5 to-[#4b95d1]/5" data-testid="company-overview-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-[#1c2e52]">
                        <TrendingUp className="w-5 h-5 text-[#4b95d1]" />
                        WAHR Institut & UG Übersicht {selectedYear}
                        <Badge variant="outline" className="ml-1 text-xs font-normal">
                          {totalUnits.toLocaleString('de-DE')} Einheiten
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="p-3 bg-white rounded-lg border border-blue-200" data-testid="institut-revenue-box">
                          <p className="text-xs text-gray-500">Institut-Umsatz (Einheiten × 2,00 €)</p>
                          <p className="text-xl font-bold text-[#1c2e52]">{formatCurrency(institutBrutto)}</p>
                          <p className="text-[11px] text-gray-500">Netto: {formatCurrency(institutNetto)} + 19% USt</p>
                        </div>
                        <div className="p-3 bg-white rounded-lg border border-amber-200" data-testid="trainer-provisions-box">
                          <p className="text-xs text-gray-500">− Trainer-Provisionen</p>
                          <p className="text-xl font-bold text-amber-700">{formatCurrency(grandTotal)}</p>
                          <p className="text-[11px] text-gray-500">Netto: {formatCurrency(grandNetto)}</p>
                        </div>
                        <div className="p-3 bg-white rounded-lg border border-emerald-300" data-testid="ug-share-box">
                          <p className="text-xs text-gray-500">= UG-Anteil</p>
                          <p className="text-xl font-bold text-emerald-700">{formatCurrency(ugBrutto)}</p>
                          <p className="text-[11px] text-gray-500">Netto: {formatCurrency(ugNetto)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Payout Reminder Card */}
                  <Card className="border-amber-300 bg-amber-50/60" data-testid="payout-reminder-card">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                          <BellRing className="w-5 h-5 text-amber-600" />
                          Auszahlungs-Erinnerung
                          {payoutReminders.length > 0 && (
                            <Badge className="bg-amber-500 text-white hover:bg-amber-500">
                              {payoutReminders.length}
                            </Badge>
                          )}
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-amber-700">Schwelle:</span>
                          <div className="relative">
                            <Input
                              type="number"
                              min="0"
                              step="50"
                              value={payoutThreshold}
                              onChange={(e) => updateThreshold(e.target.value)}
                              className="w-24 h-8 pr-6 text-right border-amber-300 bg-white"
                              data-testid="payout-threshold-input"
                            />
                            <span className="absolute right-2 top-1.5 text-xs text-gray-400">€</span>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {payoutReminders.length === 0 ? (
                        <p className="text-sm text-amber-700 flex items-center gap-2" data-testid="payout-reminder-empty">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          Keine offenen Beträge über {formatCurrency(threshold)} – alles ausgezahlt!
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {payoutReminders.map(({ trainer, open }) => (
                            <div
                              key={trainer.id}
                              className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-amber-200 cursor-pointer hover:bg-amber-50 transition-colors"
                              onClick={() => {
                                const { provs } = getTrainerSummary(trainer.id);
                                const openProv = provs.find(p => getPayoutStatus(p).remaining > 0);
                                openTrainerProvision(trainer.id, openProv?.campaign_id || provs[0]?.campaign_id);
                              }}
                              data-testid={`payout-reminder-row-${trainer.id}`}
                            >
                              <div className="flex items-center gap-2">
                                <AlertCircle className="w-4 h-4 text-amber-500" />
                                <span className="font-medium text-sm">{trainer.name}</span>
                              </div>
                              <Badge className="bg-amber-500 text-white hover:bg-amber-600 font-bold">
                                Offen: {formatCurrency(open)}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {trainers.map((trainer, idx) => {
                    const { provs, quarterly, yearTotal, yearNetto } = getTrainerSummary(trainer.id);
                    const dotColor = trainerDotColors[idx % trainerDotColors.length];
                    return (
                      <Card key={trainer.id} className="border-l-4" style={{ borderLeftColor: dotColor }} data-testid={`admin-trainer-card-${trainer.id}`}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                                style={{ backgroundColor: dotColor }}
                              >
                                {trainer.name?.charAt(0) || '?'}
                              </div>
                              {trainer.name}
                            </CardTitle>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                Netto: {formatCurrency(yearNetto)} · Brutto: {formatCurrency(yearTotal)}
                              </Badge>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openTrainerProvision(trainer.id, provs[0]?.campaign_id)}
                                data-testid={`admin-trainer-details-${trainer.id}`}
                              >
                                Details
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {provs.length === 0 && Object.keys(quarterly).length === 0 ? (
                            <p className="text-sm text-gray-500 py-2">Keine Provisionen in {selectedYear}</p>
                          ) : (
                            <div className="space-y-2">
                              {provs.map(p => {
                                const status = getPayoutStatus(p);
                                return (
                                  <div
                                    key={p.campaign_id}
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                                    onClick={() => openTrainerProvision(trainer.id, p.campaign_id)}
                                    data-testid={`admin-provision-row-${trainer.id}-${p.campaign_id}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <Badge variant="outline" className="font-mono text-xs">
                                        {getCampaignLabel(p.campaign_id)}
                                      </Badge>
                                      <span className="text-sm text-gray-600">
                                        {uniqueTraineeCount(p.trainees)} Trainees
                                      </span>
                                      <span className="text-xs text-emerald-700 hidden sm:inline" data-testid={`ug-share-${trainer.id}-${p.campaign_id}`}>
                                        UG: {formatCurrency(ugForProvision(p.total_units || 0, p.total_provision_brutto || 0, p.ug_factor, p.ug_mwst_rate))}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="text-right">
                                        <span className="font-bold text-[#1c2e52] block leading-tight">
                                          {formatCurrency(p.total_provision_brutto)}
                                        </span>
                                        <span className="text-[11px] text-gray-500">
                                          Netto: {formatCurrency(p.total_provision_netto)}
                                        </span>
                                      </div>
                                      {status.total > 0 && (
                                        status.isPaid ? (
                                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">
                                            <CheckCircle2 className="w-3 h-3 mr-1" />
                                            Ausgezahlt
                                          </Badge>
                                        ) : (
                                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs">
                                            <AlertCircle className="w-3 h-3 mr-1" />
                                            Offen: {formatCurrency(status.remaining)}
                                          </Badge>
                                        )
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                              {Object.entries(quarterly).map(([q, qData]) => (
                                <div
                                  key={`q-${q}`}
                                  className="flex items-center justify-between p-3 bg-purple-50 rounded-lg"
                                  data-testid={`admin-quarterly-row-${trainer.id}-${q}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <Badge className="bg-purple-500 text-white hover:bg-purple-500 text-xs">
                                      Q{q} - Neuwerber
                                    </Badge>
                                    <span className="text-xs text-emerald-700 hidden sm:inline">
                                      UG: {formatCurrency(ugForProvision(qData.units || 0, qData.brutto || 0, qData.ug_factor, qData.ug_mwst_rate))}
                                    </span>
                                    {qData.remaining > 0 ? (
                                      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs">
                                        <AlertCircle className="w-3 h-3 mr-1" />
                                        Offen: {formatCurrency(qData.remaining)}
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">
                                        <CheckCircle2 className="w-3 h-3 mr-1" />
                                        Ausgezahlt
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <span className="font-bold text-purple-700 block leading-tight">
                                      {formatCurrency(qData.brutto)}
                                    </span>
                                    <span className="text-[11px] text-purple-400">
                                      Netto: {formatCurrency(qData.netto)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Gehaltsrechner Tab */}
            <TabsContent value="gehaltsrechner" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-[#4b95d1]" />
                    Trainee Gehaltsrechner
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Berechne das Gehalt für Trainees basierend auf Einheiten und Arbeitszeit.
                  </p>
                  <Button
                    onClick={() => setGehaltsrechnerOpen(true)}
                    className="bg-[#4b95d1] hover:bg-[#1c2e52]"
                    data-testid="admin-open-gehaltsrechner"
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Gehaltsrechner öffnen
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Wochenplan Tab */}
            <TabsContent value="wochenplan" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#4b95d1]" />
                    Wochenplan / Dienstplan Generator
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Erstelle einen Wochenplan für Teams mit Schichten und Arbeitszeiten.
                  </p>
                  <Button
                    onClick={() => setDienstplanOpen(true)}
                    className="bg-[#4b95d1] hover:bg-[#1c2e52]"
                    data-testid="admin-open-dienstplan"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Wochenplan erstellen
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tagesplanung Tab */}
            <TabsContent value="tagesplanung" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-emerald-600" />
                    Tagesplanung - Schulungsprogramm
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    15-Tage Schulungsprogramm mit täglichen Aufgaben, Materialien und Anleitungen. Als Admin kannst du Inhalte bearbeiten und Dateien hochladen.
                  </p>
                  <Button
                    onClick={() => setTagesplanungOpen(true)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="admin-open-tagesplanung"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Tagesplanung öffnen
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Zeitstrahl Tab - Full Timeline */}
            <TabsContent value="zeitstrahl" className="mt-0">
              <TimelineDashboard embedded />
            </TabsContent>
          </Tabs>
        </div>

        {/* Modals */}
        <ProvisionManagerExtended
          open={provisionModalOpen}
          onClose={() => {
            setProvisionModalOpen(false);
            loadData();
          }}
          initialTrainer={modalTrainer}
          initialCampaign={modalCampaign}
          initialYear={parseInt(selectedYear)}
        />

        <GehaltsrechnerTrainee
          open={gehaltsrechnerOpen}
          onClose={() => setGehaltsrechnerOpen(false)}
        />

        <DienstplanGenerator
          open={dienstplanOpen}
          onClose={() => setDienstplanOpen(false)}
        />

        <TagesplanungView
          open={tagesplanungOpen}
          onClose={() => setTagesplanungOpen(false)}
        />
      </div>
    </Layout>
  );
};

export default AdminDashboard;
